import { useState, FormEvent } from 'react';

const CONTACT_INFO = [
  {
    icon: 'ri-map-pin-line',
    label: 'Address',
    value: 'NjEGOSEVA 89',
  },
  {
    icon: 'ri-building-line',
    label: 'City',
    value: 'Vranjska Banja',
  },
  {
    icon: 'ri-mail-line',
    label: 'Zip Code',
    value: '632563',
  },
  {
    icon: 'ri-global-line',
    label: 'Country',
    value: 'Serbia',
  },
  {
    icon: 'ri-mail-send-line',
    label: 'Email',
    value: 'help@nedzadoo.com',
  },
  {
    icon: 'ri-phone-line',
    label: 'Phone',
    value: '+1 6416364806',
  },
];

export default function ContactSection() {
  const [formStatus, setFormStatus] = useState<'idle' | 'sending' | 'success' | 'error'>('idle');
  const [charCount, setCharCount] = useState(0);

  const handleSubmit = async (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const form = e.currentTarget;
    const message = (form.elements.namedItem('message') as HTMLTextAreaElement)?.value || '';
    if (message.length > 500) return;

    setFormStatus('sending');
    const data = new URLSearchParams(new FormData(form) as unknown as Record<string, string>);

    try {
      const res = await fetch('https://readdy.ai/api/form/d775jl4ggtbbtt19rdng', {
        method: 'POST',
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
        body: data.toString(),
      });
      if (res.ok) {
        setFormStatus('success');
        form.reset();
        setCharCount(0);
      } else {
        setFormStatus('error');
      }
    } catch {
      setFormStatus('error');
    }
  };

  return (
    <section id="contact" className="py-28 md:py-36 bg-emerald-950">
      <div className="max-w-7xl mx-auto px-6 md:px-10">
        {/* Header */}
        <div className="text-center mb-16">
          <span className="inline-block text-xs font-semibold tracking-widest uppercase text-emerald-400 border border-emerald-700/50 rounded-full px-4 py-1.5 bg-emerald-900/30 mb-5">
            Get In Touch
          </span>
          <h2 className="text-4xl md:text-5xl font-extrabold text-white leading-tight mb-5">
            Contact Us
          </h2>
          <p className="text-emerald-200/60 text-base max-w-lg mx-auto leading-relaxed">
            Have questions about our products or want to learn more about NEDZA DOO? We&apos;d love to hear from you.
          </p>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 lg:gap-16">
          {/* Contact Info */}
          <div>
            <h3 className="text-xl font-bold text-white mb-8">Our Information</h3>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
              {CONTACT_INFO.map((item) => (
                <div
                  key={item.label}
                  className="flex items-start gap-4 bg-white/5 rounded-xl p-5 border border-white/10"
                >
                  <div className="w-9 h-9 flex items-center justify-center rounded-lg bg-emerald-500/20 shrink-0">
                    <i className={`${item.icon} text-emerald-400 text-base`}></i>
                  </div>
                  <div>
                    <div className="text-xs text-emerald-400/70 font-semibold uppercase tracking-wide mb-1">
                      {item.label}
                    </div>
                    <div className="text-white text-sm font-medium">{item.value}</div>
                  </div>
                </div>
              ))}
            </div>

            {/* Company identity footer */}
            <div className="mt-10 flex items-center gap-4">
              <img
                src="https://public.readdy.ai/ai/img_res/28134065-bed7-48fd-b9b9-1da98798da05.png"
                alt="NEDZA DOO Logo"
                className="h-10 w-auto object-contain"
              />
              <div>
                <div className="text-white font-bold text-base">NEDZA DOO</div>
                <div className="text-emerald-400/60 text-xs">iOS Development Experts</div>
              </div>
            </div>
          </div>

          {/* Contact Form */}
          <div>
            <h3 className="text-xl font-bold text-white mb-8">Send a Message</h3>
            <form
              data-readdy-form
              onSubmit={handleSubmit}
              className="flex flex-col gap-5"
            >
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
                <div className="flex flex-col gap-1.5">
                  <label className="text-xs text-emerald-300/70 font-semibold uppercase tracking-wide">
                    Full Name
                  </label>
                  <input
                    type="text"
                    name="name"
                    required
                    placeholder="John Doe"
                    className="bg-white/5 border border-white/10 rounded-lg px-4 py-3 text-white text-sm placeholder-white/30 focus:outline-none focus:border-emerald-500 transition-colors"
                  />
                </div>
                <div className="flex flex-col gap-1.5">
                  <label className="text-xs text-emerald-300/70 font-semibold uppercase tracking-wide">
                    Email Address
                  </label>
                  <input
                    type="email"
                    name="email"
                    required
                    placeholder="john@example.com"
                    className="bg-white/5 border border-white/10 rounded-lg px-4 py-3 text-white text-sm placeholder-white/30 focus:outline-none focus:border-emerald-500 transition-colors"
                  />
                </div>
              </div>

              <div className="flex flex-col gap-1.5">
                <label className="text-xs text-emerald-300/70 font-semibold uppercase tracking-wide">
                  Subject
                </label>
                <input
                  type="text"
                  name="subject"
                  required
                  placeholder="How can we help?"
                  className="bg-white/5 border border-white/10 rounded-lg px-4 py-3 text-white text-sm placeholder-white/30 focus:outline-none focus:border-emerald-500 transition-colors"
                />
              </div>

              <div className="flex flex-col gap-1.5">
                <label className="text-xs text-emerald-300/70 font-semibold uppercase tracking-wide">
                  Message
                </label>
                <textarea
                  name="message"
                  required
                  rows={5}
                  maxLength={500}
                  placeholder="Tell us more..."
                  onChange={(e) => setCharCount(e.target.value.length)}
                  className="bg-white/5 border border-white/10 rounded-lg px-4 py-3 text-white text-sm placeholder-white/30 focus:outline-none focus:border-emerald-500 transition-colors resize-none"
                />
                <div className="text-right text-xs text-emerald-400/50">{charCount}/500</div>
              </div>

              <button
                type="submit"
                disabled={formStatus === 'sending' || charCount > 500}
                className="bg-emerald-500 hover:bg-emerald-400 disabled:opacity-50 text-white font-semibold rounded-lg px-6 py-3.5 text-sm transition-colors cursor-pointer whitespace-nowrap"
              >
                {formStatus === 'sending' ? 'Sending...' : 'Send Message'}
              </button>

              {formStatus === 'success' && (
                <div className="flex items-center gap-2 text-emerald-400 text-sm bg-emerald-900/30 rounded-lg px-4 py-3 border border-emerald-700/40">
                  <i className="ri-checkbox-circle-line"></i>
                  Message sent successfully! We&apos;ll get back to you soon.
                </div>
              )}
              {formStatus === 'error' && (
                <div className="flex items-center gap-2 text-red-400 text-sm bg-red-900/20 rounded-lg px-4 py-3 border border-red-700/30">
                  <i className="ri-error-warning-line"></i>
                  Something went wrong. Please try again.
                </div>
              )}
            </form>
          </div>
        </div>

        {/* Bottom bar */}
        <div className="mt-20 pt-8 border-t border-white/10 flex flex-col sm:flex-row items-center justify-between gap-4">
          <p className="text-emerald-400/40 text-xs">
            &copy; {new Date().getFullYear()} NEDZA DOO. All rights reserved.
          </p>
          <p className="text-emerald-400/40 text-xs">
            iOS Development &bull; Privacy &bull; Performance
          </p>
        </div>
      </div>
    </section>
  );
}
